import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../lib/api";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { AsOf } from "../components/AsOf";
import { RankingAlertSignup } from "../components/RankingAlertSignup";

const when = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function FifaRanking() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "Kenya in the FIFA World Ranking, 2000 to today",
    description:
      "Every FIFA World Ranking published for Kenya since 2000, read from FIFA's own ranking feed and refreshed automatically, with the best and worst positions and how the automation works.",
    image: cardUrl("ranking"),
  });

  useEffect(() => {
    api.get("/fifa-ranking")
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load the ranking history. Refresh to try again."));
  }, []);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="fifa-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="fifa-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="fifa-ranking-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">The national game</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.title}</h1>
          <p className="body-copy mt-7 max-w-3xl">{d.standfirst}</p>
          <AsOf
            testId="fifa-source-stamp"
            kind={d.source.kind}
            asOf={d.latest?.date}
            publisher={d.source.publisher}
            url={d.source.url}
          />
        </Reveal>

        <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="fifa-headlines">
          {[
            ["Where Kenya stands", d.latest ? `#${d.latest.rank}` : "—", d.latest ? `${d.latest.points} points · ${when(d.latest.date)}` : ""],
            ["Best since 2000", d.best ? `#${d.best.rank}` : "—", d.best ? when(d.best.date) : ""],
            ["Worst since 2000", d.worst ? `#${d.worst.rank}` : "—", d.worst ? when(d.worst.date) : ""],
            ["Average position", d.average ? `#${d.average}` : "—", `${d.readings} published rankings read`],
          ].map(([label, value, note], i) => (
            <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`fifa-headline-${i}`}>
              <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
              <p className="mt-4 font-display text-3xl font-bold leading-none text-white md:text-4xl">{value}</p>
              <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 border border-zinc-900 bg-[#0b0b0b] p-5 md:p-7" data-testid="fifa-chart">
          <p className="overline">
            Ranking position, {d.from_year} to {d.to_year} — lower is better
          </p>
          <div className="mt-6 h-[320px] w-full md:h-[420px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.series} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                <CartesianGrid stroke="#1c1c1c" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => String(v).slice(0, 4)}
                  minTickGap={40}
                  tick={{ fill: "#71717a", fontSize: 10 }}
                  stroke="#27272a"
                />
                <YAxis
                  reversed
                  domain={["dataMin - 5", "dataMax + 5"]}
                  tick={{ fill: "#71717a", fontSize: 10 }}
                  stroke="#27272a"
                />
                <Tooltip
                  contentStyle={{ background: "#0b0b0b", border: "1px solid #27272a", fontSize: 12 }}
                  labelFormatter={(v) => when(v)}
                  formatter={(value, name) => [name === "rank" ? `#${value}` : value, name === "rank" ? "Rank" : "Points"]}
                />
                <Line type="monotone" dataKey="rank" stroke="#E32118" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1.1fr_1fr]">
          <div className="border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="fifa-how-it-works">
            <p className="overline text-ochre">How the automation works</p>
            <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{d.how_it_works}</p>
            <p className="mt-4 text-xs leading-relaxed text-zinc-500" data-testid="fifa-coverage-note">
              {d.coverage_note}
            </p>
            <a
              href={d.source.url}
              target="_blank"
              rel="noreferrer"
              className="mono mt-5 inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white"
              data-testid="fifa-source-link"
            >
              FIFA's ranking page <ExternalLink size={11} />
            </a>
          </div>

          <div className="overflow-x-auto border border-zinc-800" data-testid="fifa-yearly">
            <table className="w-full min-w-[420px] border-collapse text-left">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["Year", "Best", "Worst", "Average", "Rankings"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-zinc-300">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...d.yearly].reverse().map((y) => (
                  <tr key={y.year} className="border-b border-zinc-900" data-testid={`fifa-year-${y.year}`}>
                    <td className="mono px-4 py-2.5 text-sm text-white">{y.year}</td>
                    <td className="mono px-4 py-2.5 text-xs text-ochre">#{y.best}</td>
                    <td className="mono px-4 py-2.5 text-xs text-zinc-400">#{y.worst}</td>
                    <td className="mono px-4 py-2.5 text-xs text-zinc-400">#{y.average}</td>
                    <td className="mono px-4 py-2.5 text-xs text-zinc-600">{y.readings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-10">
          <RankingAlertSignup />
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/national-game" className="btn-primary" data-testid="fifa-national-link">
            The national game plan <ArrowRight size={14} />
          </Link>
          <Link to="/ranking-ambition" className="btn-ghost" data-testid="fifa-ambition-link">
            Where this plan could take the ranking
          </Link>
          <Link to="/harambee-fixtures" className="btn-ghost" data-testid="fifa-fixtures-link">
            Fixtures and results
          </Link>
          <Link to="/fkf-feed" className="btn-ghost" data-testid="fifa-feed-link">
            The FKF live feed
          </Link>
          <Link to="/sources" className="btn-ghost" data-testid="fifa-sources-link">
            Sources and Method
          </Link>
        </div>
      </div>
    </div>
  );
}
